import type { ReactNode } from 'react'

import { cn } from '@/lib/utils/cn'

/**
 * A hairline-delimited region.
 *
 * Used instead of a card with a drop shadow: depth here comes from surface elevation and a
 * 1px rule. Identical rounded cards with the same soft shadow are the generic dashboard
 * default, and they flatten hierarchy — everything looks equally important.
 */
export function Panel({
  title,
  description,
  actions,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode
  description?: ReactNode
  actions?: ReactNode
  children: ReactNode
  className?: string
  bodyClassName?: string
}) {
  return (
    <section className={cn('panel', className)}>
      {title ? (
        <header className="panel-header">
          <div className="min-w-0">
            <h2 className="truncate text-md font-semibold text-ink">{title}</h2>
            {/* Truncated to one line where there is room for one line. On a narrow screen
                the sentence is worth more than the alignment, so it wraps instead. */}
            {description ? (
              <p className="mt-0.5 text-xs text-ink-muted sm:truncate">{description}</p>
            ) : null}
          </div>
          {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
        </header>
      ) : null}
      <div className={cn(bodyClassName)}>{children}</div>
    </section>
  )
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 pb-6">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold text-ink">{title}</h1>
        {description ? (
          <p className="mt-1.5 max-w-3xl text-base text-ink-muted">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  )
}
